import * as Sentry from '@sentry/browser'
import { AnyAction, Dispatch } from 'redux'
import { ThunkAction } from 'redux-thunk'
import { createStandardAction } from 'typesafe-actions'
import wfeTokenRefresh from '../util/wfeTokenRefresh'
import { StoreState } from '../reducer/types'
import { selectFilename } from '../selectors/file'
import { selectTraceId } from '../selectors/upload'
import { getJwt } from '../util/jwtLocalstorage'
import { setFile } from './file'
import { subscribeToChannel } from './pusher'
import { ThunkResult } from './types'

export interface UploadEndEvent {
  text: string
  uploadId: string
  filename: string
}

export const ActionTypes = {
  UPLOAD_START: 'UPLOAD_START',
  UPLOAD_COMPLETE: 'UPLOAD_COMPLETE',
  UPLOAD_FAILURE: 'UPLOAD_FAILURE',
  UPLOAD_END: 'UPLOAD_END',
  CLOSE_ERROR_MESSAGE: 'CLOSE_ERROR_MESSAGE',
}

export const uploadStart = createStandardAction(ActionTypes.UPLOAD_START)()
export const uploadComplete = createStandardAction(ActionTypes.UPLOAD_COMPLETE)()
export const uploadFailed = createStandardAction(ActionTypes.UPLOAD_FAILURE)()
export const uploadEnd = createStandardAction(ActionTypes.UPLOAD_END)<UploadEndEvent>()
export const closeErrorMessage = createStandardAction(ActionTypes.CLOSE_ERROR_MESSAGE)()

interface UploadUrl {
  url: string
}

export const fetchUploadUrl = async (uploadUrl: string, headers: any, uploadId: string, fileExtension: string) =>
  await fetch(uploadUrl, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify({
      uploadId,
      fileExtension,
    }),
  })

export const getUploadUrl: ThunkAction<Promise<UploadUrl>, StoreState, null, AnyAction> = async (
  dispatch,
  getState: any,
  { config }: any
) => {
  const state = getState()
  const uploadId = state.upload.traceId
  const uploadUrl: string = config.getUploadUrl()

  if (!uploadId) {
    throw new Error('No Upload ID')
  }

  const extRegex = /(?:\.([^.]+))?$/
  let fileExtension
  if (state.file) {
    fileExtension = extRegex.exec(state.file.name)
  }

  if (!fileExtension) {
    throw new Error('No File Extension')
  }

  try {
    let headers = {
      Authorization: `Bearer ${getJwt()}`,
      'Wtai-Trace-Id': uploadId,
      'Content-Type': 'application/json',
    }

    let response = await fetchUploadUrl(uploadUrl, headers, uploadId, fileExtension[1])

    if (response.status !== 200) {
      await wfeTokenRefresh(response)
      headers.Authorization = `Bearer ${getJwt()}`
      response = await fetchUploadUrl(uploadUrl, headers, uploadId, fileExtension[1])
    }
    const url = await response.json()
    return url
  } catch (error) {
    Sentry.captureException(error)
    throw new Error('Failed to get S3 URL')
  }
}

export const onSuccessFactory = (config: any, uploadId: string, filename: string) => {
  const onUploadSuccess = config.getOnUploadSuccess()
  if (onUploadSuccess) {
    // If passed in as a prop, call this rather
    //  than redirect.
    return onUploadSuccess({
      id: uploadId,
      filename,
    })
  }

  const token = getJwt()

  const successRedirect = () => {
    const redirectUrl = `${config.editorBaseUrl}/edit/${uploadId}/?token=${token}&runChecks=true&filename=${filename}`
    window.location.assign(redirectUrl)
  }
  return successRedirect()
}

export const uploadSuccess = (url: string): ThunkResult<Promise<void>> => async (dispatch, getState, { config }) => {
  try {
    const state = getState()
    const filename = selectFilename(state)
    const uploadId = selectTraceId(state)
    const fileContents = await downloadTextFromUrl(url)
    if (fileContents) {
      dispatch(uploadEnd({ text: fileContents, filename, uploadId }))
      onSuccessFactory(config, uploadId, filename)
    }
  } catch (error) {
    console.log('Error with uploadSuccess', error)
  }
}

export const convertToFile = (content: string): ThunkResult<Promise<void>> => async dispatch => {
  const blob = new Blob([content], { type: 'text/plain' })
  const file = Object.assign(blob, { name: 'paper.txt', lastModified: new Date() }) as unknown
  dispatch(setFile(file as File))
  dispatch(subscribeToChannel())
}

export const uploadToS3: ThunkResult<Promise<void>> = async dispatch => {
  dispatch(uploadStart())

  try {
    const { url } = ((await dispatch(getUploadUrl)) as unknown) as UploadUrl
    // Not sure how to better type this.
    await dispatch(putToS3(url))
    dispatch(uploadComplete())
  } catch (error) {
    dispatch(uploadFailed())
    console.log('UPLOAD TO S3 FAILED', error)
  }
}

export const putToS3 = (url: string): ThunkResult<Promise<void>> => async (dispatch: Dispatch, getState: any) => {
  try {
    const file: File = getState().file
    await fetch(url, { method: 'PUT', body: file })
  } catch (error) {
    console.log('Error with putToS3:', error)
  }
}

export const downloadTextFromUrl = async (url: string) => {
  try {
    const response = await fetch(url)
    return await response.text()
  } catch (error) {
    console.log('Error downloading document', error)
  }
}
const SET_TRACE_ID = 'SET_TRACE_ID'

interface SetTraceIdPayload {
  traceId: string
}
export const setTraceId = createStandardAction(SET_TRACE_ID)<SetTraceIdPayload>()
